/**
 * FileIO context — where a Viewer/Modifier/Editor reads and writes its
 * file. Decouples editors from the Drive auth path so the same
 * MarkdownViewer / HtmlEditor / ExcelEditor renders in both Drive and
 * share-link contexts without parallel implementations.
 *
 * The contract is intentionally narrow: load bytes, save bytes, and a
 * synchronous `canEdit()` hint editors can read to gate edit affordances.
 *
 *   • Drive context wraps with `<FileIOProvider impl={driveFileIO}>` —
 *     routes load/save through `filesApi` exactly as today. The default
 *     value of the context IS `driveFileIO`, so existing Drive callers
 *     that don't wrap anything keep working without a provider.
 *
 *   • Share-link context wraps with `<FileIOProvider impl={shareFileIO(…)}>`
 *     — load routes through `/s/<sid>/<filename>` (the preview-serve URL
 *     the share session already authorizes); save will route through a
 *     permission-gated share-save endpoint when that ships (Phase 15b.1
 *     — for now it throws so editors can surface a clear error rather
 *     than silently losing edits).
 */
import { createContext, useContext, type ReactNode } from 'react'
import { getActiveTenantId, getActiveOrgEmail, getApiBase, sessionFetch } from '@papan/api-client'
import { filesApi } from './index.js'
import { RAW_READ_QUERY_PARAM, SERVED_FILE_TYPE_HEADER } from '@papan/types'

export interface FileIO {
  /** Load raw bytes for a file. Throws on failure. */
  loadContent(fileId: string, versionId?: string): Promise<Blob>
  /**
   * Save bytes back. The returned versionId is the new version row
   * the backend created (useful for editors that track local version
   * state for stale-write detection).
   */
  saveContent(
    fileId: string,
    content: Blob | string,
    options?: { description?: string },
  ): Promise<{ versionId: string }>
  /**
   * Synchronous hint: can edits be saved in this context?
   *   • Drive: true — the backend gates per-file (resolveFilePermission)
   *     so the editor doesn't preemptively block; failing saves surface
   *     a 403 the editor handles.
   *   • Share view-only: false — editor should suppress save affordances.
   *   • Share edit: true — editor renders the edit surface; saves
   *     route through the permission-gated share-save endpoint.
   */
  canEdit(): boolean
  /**
   * Build a streaming URL the browser can hand to a media element
   * (`<video src>` / `<audio src>` / `<iframe src>` for PDFs). Used by
   * viewers that need Range-request streaming rather than pre-loading
   * the full bytes — without this they'd buffer a multi-GB media file
   * into a JS Blob before the first frame plays.
   *
   *   • Drive: builds /api/files/<id>/content with tenant/org-email
   *     auth tokens encoded in the query string (cookies travel with
   *     `<video src>` automatically; custom headers don't).
   *   • Share: returns the share's `/s/<sid>/<filename>` URL — the share
   *     session token itself is the auth, no other params needed.
   */
  getStreamingUrl(fileId: string, opts?: { versionId?: string; fileName?: string }): string
  /**
   * The `crossorigin` attribute an element must carry to load
   * `getStreamingUrl()`'s result — `undefined` means "no attribute".
   *
   * This is NOT cosmetic. An element with no `crossorigin` attribute issues
   * a **no-cors** request, and a no-cors response is subject to the server's
   * Cross-Origin-Resource-Policy. Per Fetch, the CORP check returns
   * `allowed` immediately when the request mode is anything OTHER than
   * no-cors — so asking for a CORS request is what makes a CORP-protected
   * response loadable at all. Which value (or none) is right depends on how
   * the URL authenticates, which is exactly what a FileIO impl knows and a
   * viewer does not.
   */
  streamingCrossOrigin(): 'anonymous' | 'use-credentials' | undefined
}

/** Drive-side default. Routes everything through the standard files API. */
export const driveFileIO: FileIO = {
  loadContent: (fileId, versionId) => filesApi.getFileContent(fileId, versionId),
  saveContent: async (fileId, content, options) => {
    const result = await filesApi.saveFileContent(fileId, content, options)
    return { versionId: result.versionId }
  },
  canEdit: () => true,
  getStreamingUrl: (fileId, opts) => {
    const params = new URLSearchParams()
    const tenantId = getActiveTenantId()
    const orgEmail = getActiveOrgEmail()
    if (tenantId) params.set('tenantId', tenantId)
    if (orgEmail) params.set('orgEmail', orgEmail)
    if (opts?.versionId) params.set('version', opts.versionId)
    const qs = params.toString()
    return `${getApiBase()}/api/files/${fileId}/content${qs ? `?${qs}` : ''}`
  },
  streamingCrossOrigin: () => crossOriginForApiSubresource(),
}

/**
 * Drive streaming URLs point at `/api/*`, which the API serves with
 * `Cross-Origin-Resource-Policy: same-origin` (apps/api/src/index.ts — only
 * `/s/*` and `/sdk/*` opt out). Under clean-A (#233) the SPA and the API are
 * separate origins, so a no-cors `<video src>` load of a perfectly good 206
 * is discarded by the browser with
 * `ERR_BLOCKED_BY_RESPONSE.NotSameOrigin` (#410). Requesting CORS mode skips
 * the CORP check entirely.
 *
 * `use-credentials`, never `anonymous`: these URLs authenticate with the
 * session cookie, and `anonymous` omits it — which turns a CORP block into a
 * 401. The API already echoes the exact origin with
 * `Access-Control-Allow-Credentials: true` and exposes `Accept-Ranges` /
 * `Content-Range` / `Content-Length` for "Range-aware viewers (pdf.js,
 * video, etc.)", which is precisely what a credentialed CORS media load
 * needs; `Range` is CORS-safelisted, so seeking adds no preflight.
 *
 * Same-origin (local dev's Vite proxy, and any deployment still on the
 * Pages-Function proxy path) returns `undefined`: CORP does not apply there,
 * and the attribute would only add CORS requirements nothing satisfies.
 */
export function crossOriginForApiSubresource(): 'use-credentials' | undefined {
  if (typeof window === 'undefined') return undefined
  const base = getApiBase()
  if (!base) return undefined
  try {
    return new URL(base, window.location.href).origin === window.location.origin
      ? undefined
      : 'use-credentials'
  } catch {
    return undefined
  }
}

/**
 * #1095: a share serve URL's RAW read — the saved file's own bytes, served inert (`text/plain`) and never
 * rewritten for the preview. For reads of the file itself (the Source pane, the byte probe);
 * the preview iframe and media streams keep the plain URL.
 */
export function shareRawReadUrl(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${RAW_READ_QUERY_PARAM}=1`
}

/**
 * Share-link impl factory. Reads from the preview-serve URL the share
 * session authorizes. Save is wired in Phase 15b.1 once the
 * permission-gated share-save endpoint lands.
 */
export function shareFileIO(args: {
  /** Preview-serve URL the share session authorizes (eg. /s/<sid>/foo.md). */
  inlineUrl: string
  /** Public share token — used by the share-save endpoint in Phase 15b.1. */
  shareToken: string
  /** From share.permission — 'view' | 'edit' | 'readcopy'. */
  permission: string
}): FileIO {
  return {
    async loadContent(_fileId) {
      // #1095: the RAW read — the saved file's own bytes, unrewritten. The plain serve URL answers an HTML file with
      // the copy rewritten to run in the preview (storage shim, snapshot script…), which is not
      // the file. A raw read arrives as `text/plain`; the file's own type rides in a header.
      const res = await sessionFetch(shareRawReadUrl(args.inlineUrl), { credentials: 'same-origin' })
      if (!res.ok) throw new Error(`Load failed (${res.status})`)
      const type = res.headers.get(SERVED_FILE_TYPE_HEADER)
      const blob = await res.blob()
      return type ? new Blob([blob], { type }) : blob
    },
    async saveContent(_fileId, _content, _options) {
      // Save endpoint pending: POST /api/share-links/:token/save-edit
      // will check share.permission === 'edit' server-side and persist
      // into the owner's tenant. Until that lands, editors that try to
      // save get a clear error rather than a silent 401.
      throw new Error('Share-link edit save not yet wired.')
    },
    canEdit: () => args.permission === 'edit',
    getStreamingUrl: (_fileId, opts) => {
      // Share view streams everything through the same /s/<sid>/<path>
      // URL the preview-serve middleware authorizes. inlineUrl already
      // ends with the share's root file name; for a child of a folder
      // share we'd need to compose the relative path, but the share
      // dispatch only mounts media viewers for the rootFileId today.
      // Pass `fileName` to swap the trailing segment when the editor's
      // fileId differs from the share's root (folder shares, future).
      if (!opts?.fileName) return args.inlineUrl
      const slash = args.inlineUrl.lastIndexOf('/')
      return slash === -1
        ? args.inlineUrl
        : `${args.inlineUrl.slice(0, slash + 1)}${encodeURIComponent(opts.fileName)}`
    },
    // No attribute, deliberately. `/s/*` is the one subtree the API serves
    // with `Cross-Origin-Resource-Policy: cross-origin`, so a plain no-cors
    // media load already works from the share-view page — and the share
    // session id in the path IS the auth (no cookie travels, which is why
    // `loadContent` above can fetch it with `credentials: 'same-origin'`).
    // Asking for CORS mode here would trade a working load for a new
    // dependency on CORS headers, for nothing.
    streamingCrossOrigin: () => undefined,
  }
}

const FileIOContext = createContext<FileIO>(driveFileIO)

export function FileIOProvider({
  impl,
  children,
}: {
  impl: FileIO
  children: ReactNode
}) {
  return <FileIOContext.Provider value={impl}>{children}</FileIOContext.Provider>
}

/**
 * Read the file-IO surface. Default impl is `driveFileIO` — components
 * mounted without a provider behave as they do today, so the refactor
 * doesn't require touching every existing caller.
 */
export function useFileIO(): FileIO {
  return useContext(FileIOContext)
}

// ─── Share-session WebSocket token context ────────────────────────────
//
// Yjs's collab WebSocket auths via Better Auth cookies on the Drive
// path. Share-link recipients don't have those cookies, so the
// connection has to forward the share-session token instead — the
// server reads it from a `?share=<token>` query param on the upgrade
// URL and validates it as a share principal.
//
// This context surfaces that token to useYjs without making every
// editor accept a new prop. Drive mounts don't wrap their tree with
// the provider; useShareWsToken() returns null and the connection
// uses cookie-based auth as today. The share-view chrome wraps with
// the provider supplying the share-session id, and useYjs picks it up
// automatically — no per-editor wiring needed.

const ShareWsTokenContext = createContext<string | null>(null)

export function ShareWsTokenProvider({
  token,
  children,
}: {
  token: string | null
  children: ReactNode
}) {
  return <ShareWsTokenContext.Provider value={token}>{children}</ShareWsTokenContext.Provider>
}

export function useShareWsToken(): string | null {
  return useContext(ShareWsTokenContext)
}
